/**
 * De-identifying page-URL helpers shared by every Sentry event this app sends
 * from a handled path (reportCallableRejection, the saved-profiles near-cap
 * warning).
 *
 * The browser integration stamps the FULL page URL (query + hash), a Referer
 * header and cookies on every event. Routes here carry record ids
 * (/billing/customers/<profileId>), so an event that is meant to carry only a
 * code and a route must have those replaced before it leaves the device.
 *
 * Imports NOTHING. This file is shared by every hosting app (client,
 * client-tech, sales-client, time-client); the scope is typed structurally so
 * no bare specifier a consumer lacks can creep in (see buildIdentity.ts).
 */

/**
 * Segments that must not leave the device verbatim: anything containing a digit
 * (record ids), anything email-shaped (raw or percent-encoded `@`), and long
 * slugs (20+ chars, which is where names and titles end up).
 */
export function isIdentifyingSegment(segment: string): boolean {
  return /\d/.test(segment) || /@|%40/i.test(segment) || segment.length >= 20;
}

/**
 * The route with query, hash and identifying segments removed, so it groups and
 * holds no record identifiers.
 */
export function currentRoute(): string {
  try {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    return (
      path
        .split('/')
        .map((segment) => (segment && isIdentifyingSegment(segment) ? ':id' : segment))
        .join('/') || '/'
    );
  } catch {
    return 'unknown';
  }
}

export function currentOrigin(): string {
  try {
    return typeof window !== 'undefined' ? window.location.origin : '';
  } catch {
    return '';
  }
}

/** Origin + de-identified route: no query, no hash, no record ids. */
export function scrubbedPageUrl(): string {
  return `${currentOrigin()}${currentRoute()}`;
}

/** The slice of a Sentry event the processor rewrites. */
interface ScrubbableEvent {
  request?: {
    url?: string;
    headers?: Record<string, string>;
    query_string?: unknown;
    cookies?: unknown;
    [key: string]: unknown;
  };
  breadcrumbs?: unknown;
  [key: string]: unknown;
}

/** The slice of a Sentry scope this helper needs. */
interface ScrubbableScope {
  addEventProcessor(processor: (event: any) => any): unknown; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export interface ScrubOptions {
  /**
   * Also empty the event's breadcrumbs. Navigation/fetch breadcrumbs can carry
   * record-id URLs. Off by default: callers that have not decided to change
   * their breadcrumb behaviour keep it.
   */
  clearBreadcrumbs?: boolean;
}

/**
 * Add a scope-local event processor that, for events from this scope only:
 * rewrites `request.url` to the scrubbed page URL, drops the Referer header
 * (any casing), clears `query_string`, and drops cookies. With
 * `clearBreadcrumbs`, it also empties the breadcrumbs.
 */
export function addScrubbedRequestProcessor(scope: ScrubbableScope, options: ScrubOptions = {}): void {
  const scrubbedUrl = scrubbedPageUrl();
  const { clearBreadcrumbs = false } = options;
  scope.addEventProcessor((event: ScrubbableEvent): ScrubbableEvent => {
    const withCrumbs: ScrubbableEvent = clearBreadcrumbs ? { ...event, breadcrumbs: [] } : event;
    if (!event.request) return withCrumbs;
    const headers: Record<string, string> = {};
    for (const [name, value] of Object.entries(event.request.headers ?? {})) {
      if (name.toLowerCase() !== 'referer') headers[name] = value;
    }
    const { cookies: _cookies, ...request } = event.request;
    return { ...withCrumbs, request: { ...request, url: scrubbedUrl, headers, query_string: undefined } };
  });
}

/**
 * A URL with its query string and fragment removed AND every identifying path segment
 * replaced with ":id" (same rule as the page-route scrub above). #4557: a GCS v4 signed
 * upload URL carries its credential in the query. #6018 review: capability tokens also live
 * IN paths (/invite/<token>, /public/quote/<token>, /forms/shared/<token>,
 * /public/fleet/compliance-update/<token>, /public/recruiting/referral/<token>).
 * Keeps scheme + host. Non-strings pass through.
 */
/**
 * Routes whose NEXT segment is a capability token, masked by ROUTE whatever its length or
 * alphabet (#6018 review): a future short or all-letter token must not slip past the
 * length/digit rule.
 */
const CAPABILITY_ROUTE_PREFIXES: readonly (readonly string[])[] = [
  ['invite'],
  ['public', 'quote'],
  ['forms', 'shared'],
  ['public', 'fleet', 'compliance-update'],
  ['public', 'recruiting', 'referral'],
];

/** Index of the token segment if `segments` (leading '' for an absolute path) starts with a capability route. */
function capabilityTokenIndex(segments: readonly string[]): number {
  const offset = segments[0] === '' ? 1 : 0;
  for (const prefix of CAPABILITY_ROUTE_PREFIXES) {
    if (prefix.every((part, i) => segments[offset + i] === part) && segments.length > offset + prefix.length) {
      return offset + prefix.length;
    }
  }
  return -1;
}

export function scrubUrlValue(url: unknown): unknown {
  if (typeof url !== 'string') return url;
  const cut = url.search(/[?#]/);
  const bare = cut === -1 ? url : url.slice(0, cut);
  const origin = bare.match(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i)?.[0] ?? '';
  const segments = bare.slice(origin.length).split('/');
  const tokenAt = capabilityTokenIndex(segments);
  const scrubbedPath = segments
    .map((segment, i) => (segment && (i === tokenAt || isIdentifyingSegment(segment)) ? ':id' : segment))
    .join('/');
  return origin + scrubbedPath;
}

/** Kept for existing callers: the same scrub (it now also masks identifying path segments). */
export const stripQueryAndHash = scrubUrlValue;

/** Scrub every URL-looking token inside free text ("GET https://...?sig=x", "/invite/abc"). */
/** An absolute URL anywhere in text. */
const ABSOLUTE_URL_IN_TEXT = /[a-z][a-z0-9+.-]*:\/\/[^\s"'()<>[\]{}]+/gi;
/** A path starting at the beginning, or after whitespace, "(", "=", or a quote. */
const PATH_IN_TEXT = /(^|[\s("'=])(\/[^\s"'()<>[\]{}]*)/g;

/**
 * Scrub every URL inside free text: absolute URLs anywhere ("(https://...", "url=https://...")
 * and paths after a boundary ("GET /invite/abc"). (#6018 review: splitting on spaces missed
 * URLs glued to punctuation.)
 */
export function scrubUrlsInText(text: unknown): unknown {
  if (typeof text !== 'string') return text;
  const absolute = text.replace(ABSOLUTE_URL_IN_TEXT, (url) => scrubUrlValue(url) as string);
  return absolute.replace(PATH_IN_TEXT, (_m, lead: string, path: string) => lead + (scrubUrlValue(path) as string));
}

/** The `data` keys that carry URLs: breadcrumbs (url/from/to) and span attributes. */
const URL_DATA_KEYS: readonly string[] = ['url', 'from', 'to', 'url.full', 'http.url', 'http.target', 'url.path'];
/** Span attributes that are a URL's query or fragment on their own: dropped. */
const DROPPED_DATA_KEYS: readonly string[] = ['http.query', 'http.fragment', 'url.query', 'url.fragment'];

/** Copy of `data` with the URL-bearing keys scrubbed and query/fragment keys dropped. */
function scrubUrlData(data: unknown): unknown {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
  const out: Record<string, unknown> = { ...(data as Record<string, unknown>) };
  for (const key of URL_DATA_KEYS) {
    if (key in out) out[key] = scrubUrlValue(out[key]);
  }
  for (const key of DROPPED_DATA_KEYS) delete out[key];
  return out;
}

/**
 * The slice of a Sentry breadcrumb `scrubBreadcrumbUrls` rewrites. No index signature:
 * Sentry's `Breadcrumb` has none, so one here would make the function unassignable to
 * `beforeBreadcrumb` under the client's CI tsconfig.
 */
interface ScrubbableBreadcrumb {
  category?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Sentry types data as { [key: string]: any }
  data?: { [key: string]: any };
}

/** Breadcrumb categories whose `data` holds request or page URLs. */
const URL_BREADCRUMB_CATEGORIES: ReadonlySet<string> = new Set(['xhr', 'fetch', 'navigation']);

/**
 * `beforeBreadcrumb` for every hosting app's `Sentry.init` (#4557): scrubs the URLs in
 * xhr / fetch / navigation breadcrumbs (query, fragment, identifying path segments).
 * Sentry's default XHR instrumentation records the full request URL, so without this a
 * photo upload's signed URL (a live write credential) rode along on the next error.
 * Returns a new breadcrumb; the input is not mutated.
 */
export function scrubBreadcrumbUrls<T extends ScrubbableBreadcrumb>(breadcrumb: T): T {
  if (!breadcrumb.category || !URL_BREADCRUMB_CATEGORIES.has(breadcrumb.category) || !breadcrumb.data) {
    return breadcrumb;
  }
  return { ...breadcrumb, data: scrubUrlData(breadcrumb.data) as ScrubbableBreadcrumb['data'] };
}

/**
 * `beforeSendSpan` (#6018 review): http.client spans carry the full request URL in their
 * description and attributes; pageload/navigation spans carry the route. Returns a copy.
 */
export function scrubSpan<T extends object>(span: T): T {
  const sp = span as T & { description?: unknown; data?: unknown };
  return { ...span, description: scrubUrlsInText(sp.description), data: scrubUrlData(sp.data) } as T;
}

/**
 * Event-level URLs: the page URL on `request`, the transaction (route) name, the trace
 * context's attributes, and every span of a transaction event. Returns a copy.
 */
export function scrubEventUrls<T extends object>(event: T): T {
  const e = event as T & {
    request?: { url?: unknown; query_string?: unknown; [k: string]: unknown };
    transaction?: unknown;
    contexts?: { trace?: { data?: unknown; [k: string]: unknown }; [k: string]: unknown };
    spans?: unknown;
  };
  const out: Record<string, unknown> = { ...(event as object) };
  if (e.request) {
    const { query_string: _qs, ...request } = e.request;
    out.request = { ...request, url: scrubUrlValue(e.request.url) };
  }
  if (typeof e.transaction === 'string') out.transaction = scrubUrlsInText(e.transaction);
  if (e.contexts?.trace) {
    out.contexts = { ...e.contexts, trace: { ...e.contexts.trace, data: scrubUrlData(e.contexts.trace.data) } };
  }
  if (Array.isArray(e.spans)) {
    out.spans = e.spans.map((sp) => (sp && typeof sp === 'object' ? scrubSpan(sp as object) : sp));
  }
  return out as T;
}

/** `beforeSendTransaction`: the same scrub as any event. */
export const scrubTransactionEvent = scrubEventUrls;

/**
 * Wrap an app's `beforeSend` so the event's own URLs are scrubbed first (#6018 review: an
 * error raised on /invite/<token> shipped the token in request.url).
 */
export function withScrubbedEventUrls<E extends object, H, R>(
  beforeSend: (event: E, hint: H) => R
): (event: E, hint: H) => R {
  return (event, hint) => beforeSend(scrubEventUrls(event), hint);
}

/**
 * Replay `beforeAddRecordingEvent` (#6018 review): network and navigation entries in a
 * replay recording ("performanceSpan" frames, and replayed breadcrumbs) carry full URLs.
 * Returns a scrubbed copy; never drops the frame.
 */
export function scrubReplayRecordingEvent<T extends object>(event: T): T {
  const ev = event as T & {
    type?: unknown;
    data?: { tag?: unknown; href?: unknown; payload?: { description?: unknown; data?: unknown; [k: string]: unknown } };
  };
  // rrweb Meta event (type 4), emitted with every full snapshot: data.href is the full page
  // URL (#6018 review, delta 1).
  if (ev.type === 4 && ev.data && typeof ev.data.href === 'string') {
    return { ...event, data: { ...ev.data, href: scrubUrlValue(ev.data.href) } } as T;
  }
  const payload = ev.data?.payload;
  if (!payload || (ev.data?.tag !== 'performanceSpan' && ev.data?.tag !== 'breadcrumb')) return event;
  return {
    ...event,
    data: {
      ...ev.data,
      payload: { ...payload, description: scrubUrlsInText(payload.description), data: scrubUrlData(payload.data) },
    },
  } as T;
}

/**
 * Global event processor for REPLAY events (#6018 review, delta 1). The SDK has no
 * `beforeSendReplay`, but a replay_event runs through the event processors, and it carries
 * the session's page URLs in `urls` (and a page `request.url`). Other events pass through.
 */
export function scrubReplayEventUrls<T extends object>(event: T): T {
  const e = event as T & { type?: unknown; urls?: unknown };
  if (e.type !== 'replay_event') return event;
  const out = scrubEventUrls(event) as T & { urls?: unknown };
  return (Array.isArray(e.urls) ? { ...out, urls: e.urls.map((u) => scrubUrlValue(u)) } : out) as T;
}
